import React from 'react';
import { CheckIcon, LockClosedIcon } from '@heroicons/react/solid';

/**
 * Shared navigation primitives for the Adult Autism modular assessment modules
 * (History, SSI, Observation, Diagnostic Decision, Post-Assessment Discussion).
 *
 * WHY THIS EXISTS
 * Historically each Autism module hand-rolled its own left sidebar. They all
 * looked almost identical (numbered/lettered badges, emerald = complete,
 * brand-primary = active, grey = not started, optional lock) but the markup was
 * copy-pasted into five places, so visual tweaks had to be made five times and
 * inevitably drifted. This file is the single source of truth for that look.
 *
 * WHAT IS EXPORTED
 *   - badgeStatusClasses(status, isActive): the badge colour rules, used by
 *     every consumer (including the bespoke Observation selector) so the colour
 *     language can never drift again.
 *   - ModularNavRow: one clickable row (badge + label + completion/lock state).
 *     Flexible enough to back both the "always expanded" sidebars (History/SSI/
 *     DD/PAD) and the click-to-expand Observation sidebar.
 *   - ModularSideNavbar (default): a ready-made container that renders a list of
 *     rows for the simple, linear modules. Observation does NOT use the
 *     container (its sidebar has bespoke sections/toggles) — it composes
 *     ModularNavRow directly instead, which keeps the shared look without
 *     forcing a "god component".
 */

/**
 * Badge colour priority — identical across every Autism sidebar.
 *
 * Completion always wins (a finished step stays green even while it is the
 * active step) so the clinician can always see, at a glance, what is done.
 *
 * @param {'completed'|'started'|'default'} status
 * @param {boolean} isActive  Whether this is the currently-open step.
 * @returns {string} Tailwind classes for the badge background/text (no ring).
 */
export function badgeStatusClasses(status, isActive) {
  if (status === 'completed') return 'bg-emerald-500 text-white';
  if (isActive) return 'bg-primary text-white';
  if (status === 'started') return 'bg-primary/10 text-primary';
  return 'bg-gray-200 text-gray-500';
}

/**
 * A single navigation row: a status badge, an optional label, and an optional
 * trailing icon (a completion tick or lock by default).
 *
 * The same component covers two layout styles:
 *   - Static (default): the label is always rendered. Used by the
 *     always-expanded sidebars.
 *   - Animated (`animatedLabel`): the label is always in the DOM but its
 *     opacity/width is toggled by `showLabel`, so a parent can smoothly expand
 *     the row from a badge-only strip to a full row (Observation).
 *
 * @param {object}  props
 * @param {React.ReactNode} props.badge        Badge content (number, short id, or icon).
 * @param {React.ReactNode} [props.label]      Row label text.
 * @param {string}  [props.fullTitle]          Accessible name (aria-label; falls back to label).
 * @param {string}  [props.title]              Hover tooltip text (falls back to fullTitle/label).
 *                                             Useful in compact mode where the label is hidden.
 * @param {boolean} [props.nativeTitle=true]   Set the browser title attribute. Pass false when a
 *                                             styled tooltip is attached outside this row, so the
 *                                             two do not both appear.
 * @param {'completed'|'started'|'default'} [props.status='default']
 * @param {boolean} [props.isActive=false]     Currently-open step (adds ring + emphasis).
 * @param {boolean} [props.isLocked=false]     Locked/unavailable (dimmed, shows lock, not clickable).
 * @param {boolean} [props.isDisabled=false]   Disabled for a non-lock reason (e.g. "coming soon").
 * @param {boolean} [props.showLabel=true]     Whether the label/trailing area is visible.
 * @param {boolean} [props.animatedLabel=false] Animate label via opacity/width instead of removing it.
 * @param {'sm'|'md'} [props.badgeSize='sm']   'sm' = 28x24 rounded (expanded sidebars),
 *                                              'md' = 36x28 rounded-md (compact/Observation strip).
 * @param {() => void} [props.onClick]
 * @param {React.ReactNode} [props.trailing]   Custom trailing element (overrides the default tick/lock).
 * @param {string}  [props.badgeClassName]     Override the computed badge colour classes
 *                                              (used by Observation's indigo/amber toggle rows).
 * @param {string}  [props.rowClassName]       Extra classes appended to the row.
 */
export function ModularNavRow({
  badge,
  label,
  fullTitle,
  title,
  nativeTitle = true,
  status = 'default',
  isActive = false,
  isLocked = false,
  isDisabled = false,
  showLabel = true,
  animatedLabel = false,
  badgeSize = 'sm',
  onClick,
  trailing,
  badgeClassName,
  rowClassName,
}) {
  // A row is interactive only when it is not locked/disabled and has a handler.
  const interactive = !isLocked && !isDisabled && typeof onClick === 'function';
  // A styled tooltip owned by the parent replaces the browser's own hover
  // text. Leaving both on shows two tooltips for one row.
  const hoverTitle = nativeTitle
    ? (title || fullTitle || (typeof label === 'string' ? label : undefined))
    : undefined;
  const computedBadgeClass = badgeClassName || badgeStatusClasses(status, isActive);

  // Badge dimensions: small pill for the expanded sidebars, slightly larger
  // rounded square for the compact/Observation strip (matches the originals).
  // Compact badges grow past the 36px square when the label is wider than an
  // autism criterion id ("H/I 1" on the child ADHD symptom list). A two-character
  // id still sits in the same 36px box because of the minimum width.
  const badgeSizeClass = badgeSize === 'md'
    ? 'min-w-[2.25rem] w-max max-w-full h-7 px-1 rounded-md'
    : 'min-w-[28px] h-6 px-1.5 rounded';

  const handleClick = () => {
    if (interactive) onClick();
  };

  // Compact strip rows highlight the badge with ring + offset (same affordance as
  // the original SSI sidebar). Expanded sidebars use a full-width row border-2.
  const isCompactRow = !showLabel && !animatedLabel;

  const badgeEl = (
    <div className={`
      ${badgeSizeClass} flex items-center justify-center text-xs font-bold flex-shrink-0
      transition-all duration-200
      ${computedBadgeClass}
      ${isActive && isCompactRow ? 'ring-2 ring-primary ring-offset-1 ring-offset-white' : ''}
    `}>
      {badge}
    </div>
  );

  if (isCompactRow) {
    return (
      <div
        onClick={handleClick}
        role="button"
        tabIndex={interactive ? 0 : -1}
        aria-disabled={!interactive}
        aria-label={fullTitle || (typeof label === 'string' ? label : undefined)}
        title={hoverTitle}
        onKeyDown={(e) => {
          if (interactive && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick();
          }
        }}
        className={`
          rounded-lg flex items-center justify-center transition-all duration-200 px-1
          ${isLocked || isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${isActive ? 'shadow-sm' : interactive ? 'hover:scale-105' : ''}
          ${rowClassName || ''}
        `}
      >
        {badgeEl}
      </div>
    );
  }

  // Label visibility: in animated mode the node stays mounted (so width can
  // transition); in static mode we simply omit it when not shown.
  const labelVisibilityClass = animatedLabel
    ? (showLabel ? 'opacity-100' : 'opacity-0 w-0')
    : '';

  return (
    <div
      onClick={handleClick}
      role="button"
      tabIndex={interactive ? 0 : -1}
      aria-disabled={!interactive}
      aria-label={fullTitle || (typeof label === 'string' ? label : undefined)}
      title={hoverTitle}
      onKeyDown={(e) => {
        if (interactive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      className={`
        self-stretch px-3 py-2 rounded-lg text-left
        flex items-center gap-2 transition-all duration-200 border-2
        ${!showLabel ? 'justify-center' : ''}
        ${isLocked || isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${isActive && status === 'completed' ? 'bg-emerald-50 border-primary shadow-sm' : ''}
        ${isActive && status !== 'completed' ? 'bg-white border-primary shadow-sm' : ''}
        ${!isActive && status === 'completed' ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100' : ''}
        ${!isActive && status !== 'completed' && interactive ? 'border-transparent hover:bg-gray-100' : ''}
        ${!isActive && status !== 'completed' && !interactive ? 'border-transparent' : ''}
        ${rowClassName || ''}
      `}
    >
      {badgeEl}

      {(showLabel || animatedLabel) && (
        <div className={`
          flex-1 text-sm leading-tight text-left truncate transition-opacity duration-200
          ${isActive ? 'font-bold text-gray-900' : 'font-medium text-gray-600'}
          ${labelVisibilityClass}
        `}>
          {label}
        </div>
      )}

      {showLabel && (
        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
          {trailing !== undefined
            ? trailing
            : isLocked
              ? <LockClosedIcon className="w-3.5 h-3.5 text-gray-300" aria-hidden="true" />
              : status === 'completed'
                ? <CheckIcon className="w-4 h-4 text-emerald-500" aria-hidden="true" />
                : null}
        </div>
      )}
    </div>
  );
}

/**
 * Ready-made sidebar container for the linear Autism modules (History, SSI,
 * Diagnostic Decision, Post-Assessment Discussion). It renders a header, a list
 * of step rows, and an optional set of "extra" rows below a divider (e.g. a
 * Review link or a Back-to-previous-module link).
 *
 * Observation deliberately does not use this container — its sidebar has too
 * many bespoke sections (ADOS mode, criterion-mapping toggle, General
 * Observations) — but it reuses ModularNavRow so the rows still match.
 *
 * @param {object} props
 * @param {{title?: string, subtitle?: string}|React.ReactNode} [props.header]
 * @param {Array<{
 *   id: string,
 *   badge?: React.ReactNode,
 *   label: React.ReactNode,
 *   fullTitle?: string,
 *   status?: 'completed'|'started'|'default',
 *   isUnlocked?: boolean,
 * }>} props.items
 * @param {string} props.activeId               Id of the active step.
 * @param {(id: string) => void} props.onSelect Row click handler.
 * @param {'expanded'|'compact'} [props.mode='expanded']
 * @param {Array<{
 *   id: string,
 *   label: React.ReactNode,
 *   badge?: React.ReactNode,
 *   onClick: () => void,
 *   active?: boolean,
 *   status?: 'completed'|'started'|'default',
 *   fullTitle?: string,
 *   title?: string,
 * }>} [props.extraRows]
 * @param {boolean} [props.isNavigating=false]  Dim + disable the whole sidebar during an in-flight nav.
 * @param {string}  [props.className]
 */
export default function ModularSideNavbar({
  header,
  items = [],
  activeId,
  onSelect,
  mode = 'expanded',
  extraRows = [],
  isNavigating = false,
  className = '',
}) {
  const isCompact = mode === 'compact';

  const renderHeader = () => {
    if (!header) return null;
    // Allow either a structured {title, subtitle} or a fully custom node.
    if (React.isValidElement(header)) return header;
    if (typeof header === 'object' && (header.title || header.subtitle)) {
      return (
        <div className="text-left mb-2">
          {header.title && (
            <h3 className="text-gray-900 text-base font-bold leading-tight mb-0.5">{header.title}</h3>
          )}
          {header.subtitle && (
            <p className="text-gray-500 text-xs leading-tight">{header.subtitle}</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Compact strip: 72px width + px-2 gives the centred badge (w-9) room for
  // ring-2 + ring-offset-1 without the left/right ring being clipped by padding.
  return (
    <div
      className={`
        flex-shrink-0 border-r border-gray-200 flex flex-col sticky top-8 mt-6
        ${isCompact ? 'px-2 min-w-[72px] w-max max-w-[7.5rem] items-center' : 'px-4 min-w-[260px] max-w-[280px]'}
        ${isNavigating ? 'opacity-60 pointer-events-none' : ''}
        ${className}
      `}
    >
      {!isCompact && renderHeader()}

      <div className={`flex flex-col ${isCompact ? 'gap-2 items-center' : 'gap-0.5 flex-1 overflow-y-auto'}`}>
        {items.map((item) => {
          const isLocked = item.isUnlocked === false;
          // In compact mode a locked row shows a lock glyph inside the badge
          // (there is no room for a trailing icon); expanded rows show it trailing.
          const badge = isCompact && isLocked
            ? <LockClosedIcon className="w-3 h-3" aria-hidden="true" />
            : item.badge;

          return (
            <ModularNavRow
              key={item.id}
              badge={badge}
              label={item.label}
              fullTitle={item.fullTitle}
              title={item.title}
              status={item.status || 'default'}
              isActive={activeId === item.id}
              isLocked={isLocked}
              showLabel={!isCompact}
              badgeSize={isCompact ? 'md' : 'sm'}
              onClick={() => onSelect && onSelect(item.id)}
            />
          );
        })}

        {/* Divider + extra rows — used e.g. for Self-Reported / Other Behaviours
            under Criterion Mapping (same visual break as Task Admin's
            Tasks → Self-Report split). */}
        {extraRows.length > 0 && (
          <div className={`border-t border-gray-200 ${isCompact ? 'w-8 mx-auto mt-3 pt-3' : 'mt-2 pt-2'} flex flex-col ${isCompact ? 'gap-2 items-center' : 'gap-0.5'}`}>
            {extraRows.map((row) => (
              <ModularNavRow
                key={row.id}
                badge={row.badge}
                label={row.label}
                fullTitle={row.fullTitle || (typeof row.label === 'string' ? row.label : undefined)}
                title={row.title}
                status={row.status || 'default'}
                isActive={!!row.active}
                showLabel={!isCompact}
                badgeSize={isCompact ? 'md' : 'sm'}
                onClick={row.onClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
